import styled from "@emotion/styled";
import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import { DesignToken, IPickerListItem, Link, Picker, Text } from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { HelpCenter } from "constants/help-center";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { teamExists } from "helpers/team";
import { AutomationsRoutes } from "routes/automations/constants";
import { IAgent, selectActiveAgentsWithoutViewers } from "store/entities/agents";
import { IRuleActionAssign } from "store/entities/rules/interfaces";
import { ITeam, selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { shortPickerStyles } from "../../styles";
import { changeActionValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleActionAssign>;
}

const KEEP_ASSIGNED_TEAM_KEY = "keepAssignedTeam";
const KEEP_ASSIGNED_TEAM_OPTION: IPickerListItem = {
  key: KEEP_ASSIGNED_TEAM_KEY,
  name: "Keep assigned team",
};

const ROUND_ROBIN = {
  key: "roundRobin",
  name: "Round-robin",
};

const RANDOM_AGENT = {
  key: "random",
  name: "Random agent/admin",
};

const UNASSIGNED = {
  key: "unassigned",
  name: "Unassigned",
};

const findSelectedOption = (
  agentsOptions: IPickerListItem[],
  agentID?: string | null,
  random: boolean = false,
  roundRobin: boolean = false
): IPickerListItem | undefined => {
  if (roundRobin) {
    return ROUND_ROBIN;
  }
  if (random) {
    return RANDOM_AGENT;
  }
  if (agentID === undefined) {
    return undefined;
  }
  if (agentID === null) {
    return UNASSIGNED;
  }
  return agentsOptions.find(opt => opt.key === agentID);
};

export const Assignment: FC<IProps> = ({ input }) => {
  const location = useLocation();

  const allTeams = useSelector(selectTeamsAlphabetically);
  const allAgents = useSelector(selectActiveAgentsWithoutViewers);

  const teamOptions = [KEEP_ASSIGNED_TEAM_OPTION, ...allTeams.map(createTeamOption)];

  const keepAssignedTeamSelected = input.value.teamID === null;
  const selectedTeamExists = input.value.teamID && teamExists(allTeams, input.value.teamID);
  const selectedTeam = teamOptions.find(opt => opt.key === input.value.teamID);

  const selectedTeamOption: IPickerListItem[] = [
    ...(keepAssignedTeamSelected ? [KEEP_ASSIGNED_TEAM_OPTION] : []),
    ...(selectedTeamExists && selectedTeam ? [selectedTeam] : []),
  ];

  const offerRoundRobin = location.pathname.includes(AutomationsRoutes.Rules);
  const disableAgentPicker = input.value.teamID === undefined || (!selectedTeamExists && !keepAssignedTeamSelected);

  const availableAgents = selectedTeamExists
    ? allAgents.filter(agent => (input.value.teamID ? agent.teamIds.includes(input.value.teamID) : false))
    : allAgents;

  const agentsOptions = [
    ...(offerRoundRobin ? [ROUND_ROBIN] : []),
    RANDOM_AGENT,
    UNASSIGNED,
    ...(keepAssignedTeamSelected ? [] : availableAgents.map(createAgentOption)),
  ];

  const selectedAgent = findSelectedOption(
    agentsOptions,
    input.value.agentID,
    input.value.randomAgent,
    input.value.roundRobin
  );

  const selectedAgentOption = selectedAgent ? [selectedAgent] : [];

  const handleSelectTeam = (selected: IPickerListItem[] | null): void => {
    if (!selected) {
      return;
    }
    const teamID = selected[0].key;
    changeActionValues(
      input,
      {
        teamID: teamID === KEEP_ASSIGNED_TEAM_KEY ? null : teamID,
        agentID: undefined,
      },
      { overwrite: true }
    );
  };

  const handleSelectAgent = (selected: IPickerListItem[] | null): void => {
    if (!selected) {
      return;
    }
    const agentID = selected[0].key;

    if (agentID === ROUND_ROBIN.key) {
      return changeActionValues(input, { agentID: undefined, roundRobin: true, randomAgent: undefined });
    }
    if (agentID === RANDOM_AGENT.key) {
      return changeActionValues(input, { agentID: undefined, randomAgent: true, roundRobin: undefined });
    }
    if (agentID === UNASSIGNED.key) {
      return changeActionValues(input, { agentID: null, randomAgent: undefined, roundRobin: undefined });
    }
    return changeActionValues(input, { agentID, randomAgent: undefined, roundRobin: undefined });
  };

  return (
    <>
      <Picker
        options={teamOptions}
        selected={selectedTeamOption}
        onSelect={handleSelectTeam}
        hideClearButton
        placeholder="Select team…"
        data-testid="assignment-team-picker"
        css={shortPickerStyles}
      />
      <Picker
        options={agentsOptions}
        selected={selectedAgentOption}
        onSelect={handleSelectAgent}
        disabled={disableAgentPicker}
        hideClearButton
        placeholder="Select agent…"
        data-testid="assignment-agent-picker"
        css={shortPickerStyles}
      />
      <AssignActionExplainerContent
        agentID={input.value.agentID}
        randomAgent={input.value.randomAgent}
        roundRobin={input.value.roundRobin}
      />
    </>
  );
};

const contentMap = {
  random: "The random agent/admin assignment distributes tickets randomly among the team.",
  roundRobin: "The round-robin assignment distributes tickets equally and sequentially among the team.",
};

export const AssignActionExplainerContent: FC<{
  randomAgent?: boolean;
  roundRobin?: boolean;
  agentID?: string | null;
}> = ({ randomAgent, roundRobin, agentID }) => {
  const handleLearnMoreClick = (autoassignmentMethod: "random" | "roundRobin"): void => {
    trackEngagement("Workflows: Auto-assignment 'Learn more' clicked", EventPlace.Automations, {
      autoassignmentMethod,
    });
  };

  if (roundRobin || randomAgent) {
    return (
      <DescriptionText size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
        {contentMap[roundRobin ? "roundRobin" : "random"]}{" "}
        <Link
          bold={false}
          href={HelpCenter.AUTO_ASSIGNMENTS}
          target="_blank"
          onClick={() => handleLearnMoreClick(roundRobin ? "roundRobin" : "random")}
        >
          Learn more
        </Link>
        <br />
        <b>Note:</b> If no user in the selected team accepts auto-assignment, tickets will be assigned only to the team.
      </DescriptionText>
    );
  }

  if (!agentID) {
    return null;
  }

  return (
    <DescriptionText size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
      <b>Note:</b> If the selected user doesn&apos;t accept the auto-assignment, tickets will be assigned only to the
      team.
    </DescriptionText>
  );
};

const createTeamOption = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="single" isDefault={team.isDefault} />,
    listItemBody: <TeamPickerListItem name={team.name} pickerType="single" isDefault={team.isDefault} />,
  },
});

const createAgentOption = (agent: IAgent): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
    listItemBody: <AgentPickerListItem agentName={agent.name} avatarUrl={agent.avatar} />,
  },
});

const DescriptionText = styled(Text)`
  min-height: auto;
`;
